import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useTranslations } from '@core/js/hooks/i18n';
import { postFormData } from '@core/js/utils/fetch.util';

import { useDataFetchWithAwait } from '@interlayer/js/app/hooks/useDataFetchWithAwait';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { usePostCodeSelector } from '@interlayer/js/app/store/hooks/usePostCodeSelector';
import { setFindedAddresses } from '@interlayer/js/app/store/reducers/postCodeFinder/actions';
import {
    TAddressAdditionalParams,
    TFindedAddress,
    TFindedAddressResponse } from '@interlayer/js/app/store/reducers/postCodeFinder/types';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

export const useGetAddressById = () => {
    const dispatch = useDispatch();
    const { findedAddresses } = usePostCodeSelector();
    const t = useTranslations('playtech.mobile.system.registration.');
    const [
        dTagPostcodeConfigurationUrl,
        dTagKeyPostCode = 'GT26-FY65-WF42-YE25'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.postcodeConfigurationUrl,
        DYNAMIC_TAG_NAMES.postcodeConfigurationKey
    ]);

    const onResponse = useCallback((
        response: TFindedAddressResponse,
        additionalParams?: string
    ): TFindedAddress | undefined => {
        const items = response?.Items || [];

        if (items[0]?.Error || !response) {
            throw new Error();
        }

        if (items.length && !items[0].Error) {
            if (additionalParams) {
                dispatch(setFindedAddresses({ [additionalParams]: items[0] }));
            }

            return items[0];
        }
    }, [dispatch]);

    const onErrorPlace = useCallback(() => t('validation.zip-notFound'), [t]);

    const checkIsData = useCallback(({ additionalParams: address }: TAddressAdditionalParams): false | TFindedAddress => {
        if (address && address in findedAddresses) {
            return findedAddresses[address];
        }

        return false;
    }, [findedAddresses]);

    const { fetch, ...other } = useDataFetchWithAwait({
        checkIsData,
        onResponse,
        fetch: postFormData,
        onError: onErrorPlace
    });

    const fetchAddressById = useCallback((id?: string) => {
        if (!id) {
            return Promise.reject();
        }

        const requestBody = new FormData();
        requestBody.append('Key', dTagKeyPostCode);
        requestBody.append('Id', id);

        return fetch({
            paramsFetch: {
                url: dTagPostcodeConfigurationUrl,
                requestBody
            },
            additionalParams: id
        });
    }, [dTagKeyPostCode, dTagPostcodeConfigurationUrl, fetch]);

    return { ...other, fetchAddressById };
};
